<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import useRenditionStore from '../../stores/rendition';
import useLocale from '../../composables/useLocale';
import useUiStore from '../../stores/ui';
import useReaderStore from '../../stores/reader';

type Emits = {
  (e: 'navigate'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  direction: 'left' | 'right';
};
const props = defineProps<Props>();

const { t } = useLocale();
const { nextActive, previousActive, isRTL } = storeToRefs(useRenditionStore());
const { isChevronsVisible, isLongPress } = storeToRefs(useUiStore());
const { startPress, endPress } = useUiStore();
const { isFrogBook } = storeToRefs(useReaderStore());
const { xs } = useDisplay();

const isDisabled = computed(() => (props.direction === 'left' ? previousActive.value : nextActive.value));
const isLeft = computed(() => props.direction === 'left');
const isRight = computed(() => props.direction === 'right');
const offset = computed(() => (isChevronsVisible.value ? '0' : `-64px`)); // TODO: change to -25vw in reskin.

// Combine the logic for direction and isRTL into one computed property for the title
const title = computed(() => {
  if (isLeft.value) {
    return isRTL.value ? 'button_web_forward' : 'button_web_reverse';
  }
  return isRTL.value ? 'button_web_reverse' : 'button_web_forward';
});

const onNavigate = (event: MouseEvent | TouchEvent) => {
  if (!isLongPress.value) {
    emit('navigate');
  } else {
    // prevent ghost click after long press
    event.stopImmediatePropagation?.();
    event.preventDefault();
  }
};
/*
  TODO:
  Unsure about the @touchend event. as the interaction of long press ends when the finger is lifted. the reset should
  only happen when your done with the action in the iframe or somehow cancel the long press.
*/
</script>

<template>
  <div v-show="!isChevronsVisible" class="chevron-shadow" :class="`chevron-shadow-${direction} chevron-${direction}`" />
  <v-btn
    v-if="isFrogBook && xs"
    :id="`chevron-${direction}-extension`"
    :data-testid="`bp-chevron-${direction}-extension`"
    class="chevron chevron-extension position-absolute top-0 bg-transparent h-100 d-flex align-center"
    :class="{
      'chevron-left': isLeft,
      'chevron-right': isRight,
      'justify-start': isLeft,
      'justify-end': isRight,
    }"
    :title="t(title)"
    size="x-large"
    variant="flat"
    color="transparent"
    icon
    :ripple="false"
    :disabled="isDisabled"
    :aria-label="t(title)"
    :style="{
      [direction]: offset,
      width: '64px',
      height: '100vh',
      pointerEvents: isLongPress || isDisabled ? 'none' : 'auto',
    }"
    @mousedown="startPress"
    @touchstart="startPress"
    @mouseup="endPress(() => onNavigate($event))"
    @touchend="endPress(() => onNavigate($event))"
  />

  <v-btn
    :id="`chevron-${direction}`"
    :data-testid="`bp-chevron-${direction}`"
    class="chevron position-absolute top-0 bg-transparent h-100 d-flex align-center"
    :class="{
      'chevron-left': isLeft,
      'chevron-right': isRight,
      'justify-start': isLeft,
      'justify-end': isRight,
      'blend-difference': !isFrogBook,
      'frog-book-xs-layout': isFrogBook && xs,
    }"
    :title="t(title)"
    size="x-large"
    variant="flat"
    color="transparent"
    icon
    :ripple="false"
    :disabled="isDisabled"
    :aria-label="t(title)"
    :style="{
      [direction]: offset,
      width: '64px',
      height: '100vh',
      pointerEvents: isLongPress || isDisabled ? 'none' : 'auto',
    }"
    @mousedown="startPress"
    @touchstart="startPress"
    @mouseup="endPress(() => onNavigate($event))"
    @touchend="endPress(() => onNavigate($event))"
  >
    <v-icon
      size="x-large"
      :icon="isLeft ? 'mdi-chevron-left' : 'mdi-chevron-right'"
      :class="{ 'show-icon': isFrogBook }"
    />
  </v-btn>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

:deep(.v-ripple__container .v-ripple__animation) {
  background-color: rgba(var(--v-theme-inverse-surface), 0.3);
}

.chevron-shadow {
  width: 64px; // TOOD: change to 25vw in reskin
  &-left {
    left: 0;
  }
  &-right {
    right: 0;
  }
}

.chevron {
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  z-index: 2;
  height: 100dvh !important; //for mobile browsers
  width: 64px; // TOOD: change to 25vw in reskin
  border-radius: 0;
  .blend-difference {
    mix-blend-mode: difference;
  }

  &-right {
    right: v-bind(offset) !important;
  }
  &-left {
    left: v-bind(offset) !important;
  }
  i {
    color: transparent;
  }

  i.show-icon {
    color: rgb(var(--v-theme-on-brand));
    background-color: rgb(var(--v-theme-brand));
  }

  &:hover {
    mix-blend-mode: normal;
  }
  &:hover:before {
    background-color: transparent;
  }
  &:before {
    display: none;
  }
  &:active {
    transform: scale(0.98);
    background-color: rgba(var(--v-theme-secondary), 0.2);
    transition:
      transform 0.1s ease,
      background-color 0.1s ease;
  }
  @media (hover: hover) and (pointer: fine) and (#{map.get(v.$display-breakpoints, 'md-and-up')}) {
    &:hover i:not(.show-icon) {
      color: rgb(var(--v-theme-on-brand));
      background-color: rgb(var(--v-theme-brand));
    }
  }
}

.chevron-extension.chevron-left {
  top: 0 !important;
  height: 30dvh !important;
}
.chevron-extension.chevron-right {
  top: 0 !important;
  height: 16dvh !important;
}

.chevron.frog-book-xs-layout {
  top: 30dvh !important;
  height: calc(100dvh - 30dvh) !important;
  :deep(.v-btn__content) {
    position: absolute !important;
    top: 18.5dvh !important;
  }
}

:deep(.v-btn__overlay) {
  background-color: transparent !important;
}
</style>
